import { getDB } from "./schema.js";
import { nowIso, todayDate } from "../utils/dates.js";
import { listAllMedia } from "./media.js";
import { buildSuggestions } from "./recommendations.js";
import { buildAiring, episodeNotificationsFor, dedupeById } from "./airing.js";

// Local-only, in-app notifications surfaced by the top-bar bell. The store is
// generic ("we'll use this for multiple things"); the only producer right now is
// the release-day scan below. Notifications are device-local — never synced — and
// cleared on sign-out alongside the user's data.

const STORE = "notifications";
// Persisted high-water mark so a closed-for-days app can catch up on releases it
// missed without re-scanning all of history every launch.
const LAST_CHECKED_KEY = "notif_release_last_checked";
// Fired after any write so the bell badge (and anything else) can refresh.
export const NOTIF_EVENT = "notifications:changed";

function emitChanged() {
	window.dispatchEvent(new Event(NOTIF_EVENT));
}

// Notifications are an ARCHIVE (design_documents/history.md decision 6): the
// bell's "Clear all" only marks them `dismissed`; History → Notifications shows
// every one. Anything older than this is pruned.
export const KEEP_DAYS = 730;

const newestFirst = (a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? "");

// What the bell shows: everything not dismissed.
export async function listNotifications() {
	const db = await getDB();
	const all = await db.getAll(STORE);
	return all.filter((n) => !n.dismissed).sort(newestFirst);
}

// The whole archive (History → Notifications), dismissed included.
export async function listNotificationArchive() {
	const db = await getDB();
	return (await db.getAll(STORE)).sort(newestFirst);
}

export async function unreadCount() {
	const db = await getDB();
	const all = await db.getAll(STORE);
	return all.reduce((n, x) => n + (x.read || x.dismissed ? 0 : 1), 0);
}

// Drop notices older than KEEP_DAYS. Returns how many were removed.
export async function pruneNotifications(now = Date.now()) {
	const cutoff = new Date(now - KEEP_DAYS * 86400000).toISOString();
	const db = await getDB();
	let removed = 0;
	for (const n of await db.getAll(STORE)) {
		if ((n.created_at ?? "") < cutoff) {
			await db.delete(STORE, n.id);
			removed++;
		}
	}
	if (removed) emitChanged();
	return removed;
}

export async function markAllRead() {
	const db = await getDB();
	const tx = db.transaction(STORE, "readwrite");
	let changed = false;
	for (const n of await tx.store.getAll()) {
		if (!n.read) {
			n.read = true;
			await tx.store.put(n);
			changed = true;
		}
	}
	await tx.done;
	if (changed) emitChanged();
}

// The bell's "Clear all": hide everything from the bell (and mark it read),
// keeping it in the History archive.
export async function clearAll() {
	const db = await getDB();
	const tx = db.transaction(STORE, "readwrite");
	for (const n of await tx.store.getAll()) {
		if (!n.dismissed || !n.read) await tx.store.put({ ...n, dismissed: true, read: true });
	}
	await tx.done;
	emitChanged();
}

// Insert if the id isn't already present (ids dedupe). Returns true if inserted.
export async function addNotification(record) {
	const db = await getDB();
	if (await db.get(STORE, record.id)) return false;
	await db.put(STORE, record);
	emitChanged();
	return true;
}

// ---- release-day scan -----------------------------------------------------

// The date an item's release notification fires on: the precise release date if
// we have one (games), else the start of the release year — year-only items
// (books, TV/anime, season pseudo-items) "arrive" when their year begins, which
// is exactly when isUpcoming() flips false for them.
function arrivalDate(item) {
	// "Q4 2026" / "2027" / TBD aren't release days — no bell until it's exact.
	if (item.release_precision && item.release_precision !== "day") return null;
	if (item.release_date) return item.release_date;
	if (item.release_year) return `${item.release_year}-01-01`;
	return null;
}

// Pure core (exported for tests): the new release notifications for `items` given
// today's date, the last-checked date (null = first run) and the ids already
// notified. An item is "newly arrived" when its arrival date has passed (<= today)
// AND crossed the line since we last looked. On the very first run (lastChecked
// null) we fire ONLY for arrivals exactly today — never backfilling a library's
// worth of past releases.
export function releaseNotificationsFor(
	items,
	{ today, lastChecked, existingIds },
) {
	const seen =
		existingIds instanceof Set ? existingIds : new Set(existingIds ?? []);
	const out = [];
	for (const item of items) {
		const arrival = arrivalDate(item);
		if (arrival == null || arrival > today) continue;
		const fresh =
			lastChecked == null ? arrival === today : arrival > lastChecked;
		if (!fresh) continue;
		const id = `release:${item.id}`;
		if (seen.has(id)) continue;
		seen.add(id);
		out.push({
			id,
			type: "release",
			item_id: item.id,
			title: item.title ?? "Untitled",
			body: item.secondary ? `Out now · ${item.secondary}` : "Out now",
			route: item.route ?? null,
			cover_url: item.cover_url ?? null,
			media_type: item.media_type ?? null,
			created_at: nowIso(),
			read: false,
		});
	}
	return out;
}

// Scan the user's upcoming/incoming set (all tracked media + the recommendation
// engine's upcoming season pseudo-items — the same set that feeds Incoming) for
// newly-arrived releases and persist notifications for them. Returns the count
// created.
export async function scanReleaseNotifications() {
	const [allMedia, suggestions, airing] = await Promise.all([
		listAllMedia(),
		buildSuggestions(),
		buildAiring(),
	]);
	// Release-day arrivals: tracked media + upcoming seasons (from the
	// recommendation engine and from series you're Watching). Episodes go through
	// their own producer below so several landing together collapse into one line.
	const items = dedupeById([
		...allMedia,
		...suggestions.upcomingSeasons,
		...airing.upcomingSeasons,
	]);

	const db = await getDB();
	const existingIds = new Set((await db.getAll(STORE)).map((n) => n.id));
	const lastChecked = localStorage.getItem(LAST_CHECKED_KEY); // null on first run
	const today = todayDate();

	const fresh = [
		...releaseNotificationsFor(items, { today, lastChecked, existingIds }),
		...episodeNotificationsFor(airing.recentlyAired, { today, lastChecked, existingIds }),
	];
	for (const rec of fresh) await db.put(STORE, rec);

	localStorage.setItem(LAST_CHECKED_KEY, today);
	if (fresh.length > 0) emitChanged();
	return fresh.length;
}
